import { useCallback } from 'react';

import { useQuery } from '@atlassian/conversation-assistant-service/common/utils/use-query';

import {
	useSpaceConversationsError,
	useSpaceConversationsLoading,
	useSpaceConversationsPage,
	useSpaceStoreActions,
} from '../../controllers/space-store/hooks';
import type { SpaceConversationsResponse } from '../../controllers/space-store/types';

type UseGetSpaceConversationsProps = {
	spaceId: string;
	cloudId: string;
	product: string;
	experienceId: string;
	/** Pagination offset. When omitted, the server default is used. */
	offset?: number;
	/** Number of conversations to fetch. When omitted, the server default is used. */
	count?: number;
	/** When false the hook will not fire an HTTP request. Defaults to true. */
	enabled?: boolean;
};

type UseGetSpaceConversationsResult = {
	conversationPage: SpaceConversationsResponse | null;
	isLoading: boolean;
	error: Error | null;
	refetch: () => Promise<void>;
};

/**
 * Reads the per-space conversation page from the spaces sweet-state store,
 * fetching from the BE on mount / when params change. Wraps the shared
 * `useQuery` for fetch lifecycle (mount, key-change, abort on unmount) but
 * keeps the spaces sweet-state store as the canonical home for the data.
 *
 * Public API is unchanged from the previous local-`useState` implementation.
 */
export const useGetSpaceConversations = ({
	spaceId,
	cloudId,
	product,
	experienceId,
	offset,
	count,
	enabled = true,
}: UseGetSpaceConversationsProps): UseGetSpaceConversationsResult => {
	const { fetchSpaceConversations } = useSpaceStoreActions();
	const conversationPage = useSpaceConversationsPage(spaceId);
	const isLoading = useSpaceConversationsLoading(spaceId);
	const error = useSpaceConversationsError(spaceId);

	const { refetch: refetchQuery } = useQuery<void>({
		queryKey: [
			'rovo-spaces:getSpaceConversations',
			spaceId,
			cloudId,
			product,
			experienceId,
			// Stringify-safe pagination keys so a missing param yields a
			// stable distinct cache key (vs. `undefined`, which the
			// `queryKey.map(toString)` step would coerce to the literal
			// string 'undefined' anyway, but being explicit avoids surprises
			// if the helper changes).
			offset ?? -1,
			count ?? -1,
		],
		queryFn: () =>
			fetchSpaceConversations({
				spaceId,
				cloudId,
				product,
				experienceId,
				offset,
				count,
			}),
		enabled,
	});

	const refetch = useCallback(async () => {
		await refetchQuery();
	}, [refetchQuery]);

	return { conversationPage, isLoading, error, refetch };
};
